import { Dispatch, useContext, useEffect } from 'react';
import { isEmpty } from 'lodash-es';

import { Action, AppState, AppStateContext } from '@/state/AppStateContext';
import { checkPowerpointStatus, checkSlideStructureStatus, checkStatus, checkMarketingStatus, getUsersAgents, getUsersCustomGPTs, trackEvent } from '@/api/api';
import { Agent, ChatModel, Conversation, CustomGPT, SlideStructure } from '@/api/models';
import { chatModels } from '@/constants/models';
import { textareaRef } from '@/components/PromptInput/promptInputRef';
import { applyPersistedModelSelection, persistModelSelection } from '@/utils/modelSelection';
import { checkFileProcessingStatus } from '@/utils/fileUploadStatus';

import anthropic_logo from "@/assets/providers/anthropic.svg";
import auto_logo from "@/assets/logo.png";
import deepseek_logo from "@/assets/providers/deepseek.svg";
import openai_light_logo from "@/assets/providers/openai_light.svg";
import openai_logo from "@/assets/providers/openai.svg";
import moonshotai_logo from "@/assets/providers/moonshotai.svg";

type AppContextType = {
  state: AppState;
  dispatch: Dispatch<Action>;
} | undefined;

export const setTitle = (appStateContext: AppContextType, title: string) => {
  let env = '';
  if (appStateContext?.state.frontendSettings?.display_env == 'DEV' || appStateContext?.state.frontendSettings?.display_env == 'TST') env = " - " + appStateContext.state.frontendSettings.display_env;
  if (!isEmpty(title)) document.title = title + ' | ALLY Chat | SMS group' + env;
  else document.title = 'ALLY Chat | SMS group' + env;
}

export const getLogo = (appStateContext: AppContextType, brand: string) => {
  if (appStateContext?.state.theme === 'light') {
    if (brand === 'Anthropic') return anthropic_logo;
    else if (brand === 'DeepSeek') return deepseek_logo;
    else if (brand === 'OpenAI') return openai_light_logo;
    else if (brand === 'Moonshot AI') return moonshotai_logo;
    else return auto_logo;
  } else {
    if (brand === 'Anthropic') return anthropic_logo;
    else if (brand === 'DeepSeek') return deepseek_logo;
    else if (brand === 'OpenAI') return openai_logo;
    else if (brand === 'Moonshot AI') return moonshotai_logo;
    else return auto_logo;
  }
}

// Clears file inputs and citation state only — does NOT touch currentCustomGPT, currentAgent, or selectedTool
export const clearChatTransientState = (appStateContext: AppContextType, preserveUploads = false) => {
  // preserveUploads keeps the pending file/image selections (the input badges) intact.
  // Used when changing model in a regular chat: a new chat is started but the files the
  // user already picked should carry over rather than being silently dropped.
  if (!preserveUploads) {
    if (appStateContext?.state.fileUpload?.current) appStateContext.state.fileUpload.current.value = '';
    if (appStateContext?.state.imageUpload?.current) appStateContext.state.imageUpload.current.value = '';
  }
  appStateContext?.dispatch({ type: 'SET_INDIVIDUAL_FILE_CHAT', payload: null });
  appStateContext?.dispatch({ type: 'SET_SELECTED_CITATION', payload: null });
  appStateContext?.dispatch({ type: 'SET_SELECTED_CITATIONS', payload: null });
}

export const clearCustomGPT = (appStateContext: AppContextType) => {
  appStateContext?.dispatch({ type: 'SET_CURRENT_CUSTOM_GPT', payload: null });
  localStorage.setItem('sms-gpt_cgpt', '');
}

export const clearAgent = (appStateContext: AppContextType) => {
  appStateContext?.dispatch({ type: 'SET_CURRENT_AGENT', payload: null });
  localStorage.setItem('sms-gpt_agent', '');
}

export const changeModel = (appStateContext: AppContextType, model: ChatModel) => {
  const nextModel = model;
  appStateContext?.dispatch({ type: 'SET_CURRENT_MODEL', payload: nextModel });
  localStorage.setItem('sms-gpt_model', nextModel.id);
  persistModelSelection(nextModel);
  // Starting a new chat, but keep any files the user already selected for upload.
  newChat(appStateContext, true);
  setTimeout(() => {
    textareaRef?.current?.focus();
  }, 500);
}

/**
 * Apply a deployment selected by the backend without starting a new chat.
 * Upload-triggered requests use the returned model synchronously because React
 * state updates are asynchronous. Returns undefined when the deployment isn't
 * a known model — callers should fall back to the current/default model rather
 * than treat this as fatal.
 */
export const resolveModelByDeployment = (deployment: string): ChatModel | undefined => {
  const model = chatModels.find(item => item.deployment === deployment);
  if (!model) return undefined;
  return applyPersistedModelSelection(model) || model;
}

// Applies a backend-forced model override for the CURRENT conversation only (e.g. the
// server routing a tabular file upload onto its configured Responses deployment). This is
// intentionally conversation-scoped: it must never touch the persistent global default
// (localStorage 'sms-gpt_model' / persistModelSelection), which only `changeModel` and
// friends should write. Returns undefined when the deployment can't be resolved to a known
// model, in which case nothing is dispatched and the caller should keep using its current model.
export const setEffectiveModel = (appStateContext: AppContextType, deployment: string): ChatModel | undefined => {
  const effectiveModel = resolveModelByDeployment(deployment);
  if (!effectiveModel) return undefined;

  appStateContext?.dispatch({ type: 'SET_CURRENT_MODEL', payload: effectiveModel });

  const currentChat = appStateContext?.state.currentChat;
  if (currentChat) {
    const updatedChat = { ...currentChat, model: effectiveModel };
    appStateContext.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: updatedChat });
    appStateContext.dispatch({
      type: currentChat.isGroup ? 'UPDATE_GROUP_CHAT' : 'UPDATE_CHAT_HISTORY',
      payload: updatedChat,
    });
  }

  return effectiveModel;
}

// Group chats keep one shared conversation; changing model must NOT start a new chat or
// reset the UI. The selection is only used for the next message the user sends (and is not
// persisted server-side), so we just update the current-model state.
export const setModelForMessage = (appStateContext: AppContextType, model: ChatModel) => {
  appStateContext?.dispatch({ type: 'SET_CURRENT_MODEL', payload: model });
  localStorage.setItem('sms-gpt_model', model.id);
  persistModelSelection(model);
}

// A Custom GPT can define a default model. When one is selected to chat with, apply that model
// as the current model (persisted like a normal model change) so it is pre-selected. The user can
// still switch models afterwards. No-op when the GPT has no default model or it is no longer valid.
const applyCustomGPTDefaultModel = (appStateContext: AppContextType, customGPT: CustomGPT) => {
  if (isEmpty(customGPT.default_model)) return;
  // default_model stores the model's id; it is resolved to the full ChatModel here so the correct
  // model details (deployment, api family, etc.) are carried through when the prompt is sent.
  const model = chatModels.find(m => m.id === customGPT.default_model);
  if (!model) return;
  appStateContext?.dispatch({ type: 'SET_CURRENT_MODEL', payload: applyPersistedModelSelection(model) });
  localStorage.setItem('sms-gpt_model', model.id);
  persistModelSelection(model);
}

export const changeCustomGPT = (appStateContext: AppContextType, customGPT: CustomGPT) => {
  appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: null });
  appStateContext?.dispatch({ type: 'SET_CURRENT_CUSTOM_GPT', payload: customGPT });
  applyCustomGPTDefaultModel(appStateContext, customGPT);
  localStorage.setItem('sms-gpt_cgpt', customGPT.id);
  clearAgent(appStateContext);
  newChat(appStateContext);
  setTimeout(() => {
    textareaRef?.current?.focus();
  }, 500);
}

export const changeAgent = (appStateContext: AppContextType, agent: Agent) => {
  const hadCustomGPT = !isEmpty(appStateContext?.state.currentCustomGPT);
  appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: null });
  appStateContext?.dispatch({ type: 'SET_CURRENT_AGENT', payload: agent });
  localStorage.setItem('sms-gpt_agent', agent.id);
  if (hadCustomGPT) {
    newChat(appStateContext);
    clearCustomGPT(appStateContext);
  }
  setTimeout(() => {
    textareaRef?.current?.focus();
  }, 500);
}

// Image generation is only available on the Responses family (OpenAI GPT). The backend no
// longer reroutes non-image models to the image model, so entering image mode must move the
// user onto an image-capable model. The switch is persisted and reflected in the model
// dropdown (ImageRoutingRedesign 4d) so follow-up edits stay on the image model; it does not
// start a new chat, keeping the current thread intact for multi-turn editing.
const ensureImageCapableModel = (appStateContext: AppContextType) => {
  const current = appStateContext?.state.currentModel;
  if (current && current.provider_api_family === 'responses') return;
  const imageModel = chatModels.find(m => m.default && m.provider_api_family === 'responses')
    ?? chatModels.find(m => m.provider_api_family === 'responses');
  if (!imageModel) return;
  appStateContext?.dispatch({ type: 'SET_CURRENT_MODEL', payload: applyPersistedModelSelection(imageModel) });
  localStorage.setItem('sms-gpt_model', imageModel.id);
  persistModelSelection(imageModel);
}

export const selectTool = (appStateContext: AppContextType, tool: string) => {
  appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: tool });
  clearCustomGPT(appStateContext);
  clearAgent(appStateContext);
  if (tool === 'image_generation') ensureImageCapableModel(appStateContext);
  setTimeout(() => {
    textareaRef?.current?.focus();
  }, 500);
}

export const selectCustomGPT = (appStateContext: AppContextType, customGPT: CustomGPT) => {
  appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: null });
  appStateContext?.dispatch({ type: 'SET_CURRENT_CUSTOM_GPT', payload: customGPT });
  applyCustomGPTDefaultModel(appStateContext, customGPT);
  localStorage.setItem('sms-gpt_cgpt', customGPT.id);
  clearAgent(appStateContext);
  setTimeout(() => {
    textareaRef?.current?.focus();
  }, 500);
}

export const openChat = (appStateContext: AppContextType, item: Conversation | null, shared: boolean = false, publicCustomGPT: CustomGPT | null = null) => {
  if (item) {
    if (!shared) appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: isEmpty(item.selected_tool) ? null : (item.selected_tool || null) });
    clearChatTransientState(appStateContext);
    clearCustomGPT(appStateContext);
    clearAgent(appStateContext);
    if (!isEmpty(item.customGPTID) && (publicCustomGPT || appStateContext?.state.customGPTs?.find(customGPT => customGPT.id === item.customGPTID))) {
      if (publicCustomGPT) appStateContext?.dispatch({ type: 'SET_CURRENT_CUSTOM_GPT', payload: publicCustomGPT });
      else appStateContext?.dispatch({ type: 'SET_CURRENT_CUSTOM_GPT', payload: appStateContext?.state.customGPTs?.find(customGPT => customGPT.id === item.customGPTID) || null });
      localStorage.setItem('sms-gpt_cgpt', item.customGPTID);
      appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: null });
    }
    appStateContext?.dispatch({ type: 'SET_CURRENT_MODEL', payload: applyPersistedModelSelection(item.model) });
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: item });
    if (screen.width > 700) appStateContext?.dispatch({ type: 'SET_CONV_FILES_OPEN', payload: true });
    appStateContext?.dispatch({ type: 'SET_CHATPANEL_TRANSITIONED', payload: true });
    textareaRef?.current?.focus();
  }
}

export const newChat = (appStateContext: AppContextType, preserveUploads = false) => {
  clearChatTransientState(appStateContext, preserveUploads);
  appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });
  appStateContext?.dispatch({ type: 'SET_CHATPANEL_TRANSITIONED', payload: false });
  textareaRef?.current?.focus();
}

export const resetChat = (appStateContext: AppContextType) => {
  clearChatTransientState(appStateContext);
  appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: null });
  appStateContext?.dispatch({ type: 'SET_CURRENT_MODEL', payload: applyPersistedModelSelection(chatModels.find(m => m.default) || chatModels[0]) });
  localStorage.setItem('sms-gpt_model', (chatModels.find(m => m.default) || chatModels[0]).id);
  clearCustomGPT(appStateContext);
  clearAgent(appStateContext);
  appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });
  appStateContext?.dispatch({ type: 'SET_CHATPANEL_TRANSITIONED', payload: false });
  textareaRef?.current?.focus();
}

export const refreshUserCustomGPTs = async (appStateContext: AppContextType) => {
  appStateContext?.dispatch({ type: 'SET_CUSTOM_GPTS_LOADED', payload: false });
  const customGPTs = await getUsersCustomGPTs();
  appStateContext?.dispatch({ type: 'LOAD_CUSTOM_GPTS', payload: customGPTs as unknown as CustomGPT[] });
  if (!isEmpty(appStateContext?.state.currentCustomGPT)) {
    if (isEmpty(customGPTs.find(c => c.id === appStateContext?.state.currentCustomGPT?.id))) {
      appStateContext?.dispatch({ type: 'SET_CURRENT_MODEL', payload: (chatModels.find(m => m.default) || chatModels[0]) });
      clearCustomGPT(appStateContext);
    }
  }
  appStateContext?.dispatch({ type: 'SET_CUSTOM_GPTS_LOADED', payload: true });
}

export const refreshUserAgents = async (appStateContext: AppContextType) => {
  const agents = await getUsersAgents();
  appStateContext?.dispatch({ type: 'LOAD_AGENTS', payload: agents as unknown as Agent[] });
  if (!isEmpty(appStateContext?.state.currentAgent)) {
    if (isEmpty(agents.find(a => a.id === appStateContext?.state.currentAgent?.id))) clearAgent(appStateContext);
  }
}

export const toggleHistoryPanel = (appStateContext: AppContextType) => {
  appStateContext?.dispatch({ type: 'SET_SIDEPANEL_TRANSITIONING', payload: true });

  if (localStorage.getItem('sms-gpt_sidepanelOpen') === null || localStorage.getItem('sms-gpt_sidepanelOpen') === '1') {
    localStorage.setItem('sms-gpt_sidepanelOpen', '0');
    appStateContext?.dispatch({ type: 'SET_SIDEPANEL', payload: false });
  } else {
    localStorage.setItem('sms-gpt_sidepanelOpen', '1');
    appStateContext?.dispatch({ type: 'SET_SIDEPANEL', payload: true });
  }
}

export const closeSidebarOnMobile = (appStateContext: AppContextType) => {
  if (screen.width > 700 || !appStateContext?.state.sidepanelOpen) return;
  localStorage.setItem('sms-gpt_sidepanelOpen', '0');
  appStateContext?.dispatch({ type: 'SET_SIDEPANEL', payload: false });
}

export const toggleTheme =(appStateContext: AppContextType) => {
  var newTheme = 'dark';
  if (appStateContext?.state.theme === 'dark') newTheme = 'light';

  localStorage.setItem('sms-gpt_theme', newTheme);
  appStateContext?.dispatch({ type: 'TOGGLE_THEME' });
  document.documentElement.setAttribute('data-theme', newTheme);
  if (newTheme == 'dark') document.documentElement.classList.add('dark');
  else document.documentElement.classList.remove('dark');
}

export const useHandleTransitionEnd = () => {
  const appStateContext = useContext(AppStateContext);

  useEffect(() => {
    const handleTransitionEnd = () => appStateContext?.dispatch({ type: 'SET_SIDEPANEL_TRANSITIONING', payload: false });

    const contentElement = document.querySelector('.content');
    contentElement?.addEventListener('transitionend', handleTransitionEnd);

    return () => contentElement?.removeEventListener('transitionend', handleTransitionEnd);
  }, [appStateContext]);
}

export const handleCheckStatus = async (statusURL: string) => {
  return checkFileProcessingStatus(statusURL, checkStatus);
}

export const handleCheckMarketingStatus = async (guid: string) => {
  try {
    const response = await checkMarketingStatus(guid);
    const data = await response?.json();

    if (data['res']['status'] === "completed") return true;
    return false;
  } catch (error) {
    return true;
  }
}

export const handleCheckPowerpointStatus = async (statusURL: string, presentationId?: string) => {
  try {
    const response = await checkPowerpointStatus(statusURL, presentationId);
    const data = await response?.json();

    // results come back as a string while running, then as an object when complete
    let res;
    if (typeof data.res === 'string') res = JSON.parse(data.res);
    else res = data.res;

    if (res['runtimeStatus'] === "Completed") {
      return { complete: true, success: true, downloadUrl: data.res.download_url || res.download_url || null };
    }
    if (res['runtimeStatus'] === "Failed") {
      return { complete: true, success: false, downloadUrl: null };
    }
    return { complete: false, success: false, downloadUrl: null };
  } catch (error) {
    return { complete: false, success: false, downloadUrl: null };
  }
}

export const handleCheckSlideStructureStatus = async (statusURL: string): Promise<{ complete: boolean; success: boolean; slideJson: SlideStructure | null }> => {
  try {
    const response = await checkSlideStructureStatus(statusURL);
    const data = await response?.json();

    // results come back as a string while running, then as an object when complete
    let res;
    if (typeof data.res === 'string') res = JSON.parse(data.res);
    else res = data.res;

    if (res['runtimeStatus'] === "Completed") {
      return { complete: true, success: true, slideJson: data.res.slide_json || res.slide_json || null };
    }
    if (res['runtimeStatus'] === "Failed") {
      return { complete: true, success: false, slideJson: null };
    }
    return { complete: false, success: false, slideJson: null };
  } catch (error) {
    return { complete: true, success: false, slideJson: null };
  }
}

export const doTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => {
  trackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
}
